import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import {
  benefitsText,
  CUSTOM_MAX_CENTS,
  CUSTOM_MIN_CENTS,
  CUSTOM_PLAN,
  customAmountError,
  customPlan,
  formatCredits,
  formatEur,
  getPlan,
  parseEurToCents,
  planText,
  plans,
} from "@/lib/plans";
import { goToPayPage } from "@/lib/payHost";
import CardBrands from "./components/CardBrands";
import OrderReview from "./components/OrderReview";
import PromoCodeField from "./components/PromoCodeField";
import { quotePromo, type AppliedPromo } from "./promo";
import MerchantOfRecord from "@/components/MerchantOfRecord";
import { api, ApiError } from "@/lib/api";
import Logo from "@/pages/home/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import Footer from "@/pages/home/components/Footer";
import { loadCheckoutDetails, saveCheckoutDetails } from "./checkoutDetails";
import { currentLanguage } from "@/i18n";

/** Mirrors SERVED_COUNTRIES in server/src/lib/countries.ts — the server rejects any other. */
const COUNTRIES = [
  "Bulgaria",
  "Austria",
  "Belgium",
  "Croatia",
  "Cyprus",
  "Czechia",
  "Denmark",
  "Estonia",
  "Finland",
  "France",
  "Germany",
  "Greece",
  "Hungary",
  "Ireland",
  "Italy",
  "Latvia",
  "Lithuania",
  "Luxembourg",
  "Malta",
  "Netherlands",
  "Norway",
  "Poland",
  "Portugal",
  "Romania",
  "Slovakia",
  "Slovenia",
  "Spain",
  "Sweden",
  "Switzerland",
  "United Kingdom",
  "United States",
  "Canada",
  "Australia",
];

interface Form {
  fullName: string;
  email: string;
  phone: string;
  company: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postalCode: string;
  country: string;
}

/** Translation keys (under `checkout.errors`) for each invalid field. */
type Errors = Partial<Record<keyof Form, string>>;

const emptyForm: Form = {
  fullName: "",
  email: "",
  phone: "",
  company: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  postalCode: "",
  country: "Bulgaria",
};

const inputClass =
  "w-full rounded-lg bg-background-50 border border-background-300/60 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent";
const labelClass = "block text-sm font-medium text-foreground-700 mb-1.5";

function validate(form: Form): Errors {
  const errors: Errors = {};
  if (!form.fullName.trim()) errors.fullName = "fullNameRequired";
  if (!form.email.trim()) errors.email = "emailRequired";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
    errors.email = "emailInvalid";
  if (!form.phone.trim()) errors.phone = "phoneRequired";
  else if (form.phone.replace(/\D/g, "").length < 6)
    errors.phone = "phoneInvalid";
  if (!form.addressLine1.trim()) errors.addressLine1 = "addressLine1Required";
  if (!form.city.trim()) errors.city = "cityRequired";
  if (!form.postalCode.trim()) errors.postalCode = "postalCodeRequired";
  if (!form.country.trim()) errors.country = "countryRequired";
  return errors;
}

export default function CheckoutPage() {
  const { t } = useTranslation();
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const planName = searchParams.get("plan");
  const plan = useMemo(() => getPlan(planName), [planName]);
  const text = planText(t, plan);

  // The custom plan: the customer's own price, kept in the URL so a reload or
  // the sign-in round trip comes back to the same amount.
  const isCustom = planName?.toLowerCase() === CUSTOM_PLAN.toLowerCase();
  const customAmount = searchParams.get("amount") ?? "";
  const customCents = parseEurToCents(customAmount);
  const customError = isCustom ? customAmountError(t, customCents) : null;
  const custom = isCustom && customCents !== null && !customError ? customPlan(customCents) : null;

  /** What the order summary and the pay button show, for a fixed or custom plan. */
  const line = isCustom
    ? {
        title: t("checkout.custom.planName"),
        priceCents: custom?.amountCents ?? 0,
        credits: custom
          ? t("checkout.custom.credits", { count: custom.credits, formatted: formatCredits(custom.credits) })
          : "—",
        features: custom
          ? [t("checkout.custom.levelBenefits", { level: custom.level.name }), ...benefitsText(t, custom.level)]
          : [],
      }
    : {
        title: t("checkout.summary.planName", { plan: plan.name }),
        priceCents: plan.priceCents,
        credits: t("checkout.summary.billing", { credits: text.credits }),
        features: text.features,
      };

  const checkoutPath = isCustom
    ? `/checkout?plan=${CUSTOM_PLAN}&amount=${encodeURIComponent(customAmount)}`
    : `/checkout?plan=${encodeURIComponent(plan.name)}`;

  const [form, setForm] = useState<Form>(emptyForm);
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);
  // A server message as received, or null with `submitFailed` for our own.
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitFailed, setSubmitFailed] = useState(false);
  // Step two: the details are valid and shown back read-only for a final
  // check before "Order now" sends the customer to pay.
  const [reviewing, setReviewing] = useState(false);
  // 18+ and the Terms, Refund and Delivery policies. Ticked fresh each time:
  // the server records the acceptance with the order.
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [termsError, setTermsError] = useState(false);

  // A promo code the server accepted for this plan. The server prices it again
  // when the order is placed; what is shown here is never sent.
  const tier = isCustom ? CUSTOM_PLAN : plan.name;
  const quoteAmount = custom ? (custom.amountCents / 100).toFixed(2) : undefined;
  const [promo, setPromo] = useState<AppliedPromo | null>(null);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [promoChecking, setPromoChecking] = useState(false);
  // Only the latest check may update the page; a slower earlier one is dropped.
  const promoRequest = useRef(0);
  const totalCents = promo ? promo.totalCents : line.priceCents;

  const applyPromo = useCallback(
    async (code: string) => {
      const request = ++promoRequest.current;
      setPromoChecking(true);
      setPromoError(null);
      try {
        const result = await quotePromo(code, tier, quoteAmount);
        if (request !== promoRequest.current) return;
        if ("promo" in result) {
          setPromo(result.promo);
        } else {
          setPromo(null);
          setPromoError(
            t(`checkout.promo.errors.${result.reason}`, {
              amount: formatEur(result.minOrderCents ?? 0),
            })
          );
        }
      } catch (err) {
        if (request !== promoRequest.current) return;
        setPromo(null);
        // A rate limit or similar comes with the server's own message.
        setPromoError(err instanceof ApiError && err.status === 429 ? err.message : t("checkout.promo.errors.failed"));
      } finally {
        if (request === promoRequest.current) setPromoChecking(false);
      }
    },
    [t, tier, quoteAmount]
  );

  const removePromo = () => {
    promoRequest.current++;
    setPromo(null);
    setPromoError(null);
    setPromoChecking(false);
  };

  // A discount is worked out for one price: re-check the code when the plan
  // or the custom amount changes.
  const promoCode = promo?.code;
  useEffect(() => {
    if (promoCode) void applyPromo(promoCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tier, quoteAmount]);

  // Signing in is optional. When signed in, prefill contact details from the
  // account; otherwise the credits go to the account for the email entered.
  useEffect(() => {
    if (!user) return;
    setForm((prev) => ({
      ...prev,
      fullName: prev.fullName || profile?.full_name || "",
      email: prev.email || user.email || "",
    }));
  }, [user, profile]);

  const setField = (key: keyof Form) => (value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const selectPlan = (name: string) => {
    setSearchParams({ plan: name }, { replace: true });
  };

  const selectCustom = (amount = customAmount || "50") => {
    setSearchParams({ plan: CUSTOM_PLAN, amount }, { replace: true });
  };

  // Restore what was typed when the customer comes back from the bank picker.
  useEffect(() => {
    const saved = loadCheckoutDetails();
    if (!saved) return;
    const { customer } = saved;
    setForm((prev) => ({
      ...prev,
      fullName: customer.fullName,
      email: customer.email,
      phone: customer.phone,
      company: customer.company,
      addressLine1: customer.address.line1,
      addressLine2: customer.address.line2,
      city: customer.address.city,
      postalCode: customer.address.postalCode,
      country: customer.address.country || prev.country,
    }));
    if (saved.promoCode) void applyPromo(saved.promoCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    if (customError) return;

    const nextErrors = validate(form);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setReviewing(false);
      return;
    }
    if (!acceptTerms) {
      setTermsError(true);
      return;
    }

    if (!reviewing) {
      setReviewing(true);
      window.scrollTo({ top: 0, behavior: "instant" });
      return;
    }

    const details = {
      tier,
      // Euros with two decimals; the server re-checks the range and works out
      // what the amount buys.
      amount: quoteAmount,
      promoCode: promo?.code,
      customer: {
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        company: form.company.trim(),
        address: {
          line1: form.addressLine1.trim(),
          line2: form.addressLine2.trim(),
          city: form.city.trim(),
          postalCode: form.postalCode.trim(),
          country: form.country,
        },
      },
      acceptTerms: true,
    };
    saveCheckoutDetails(details);

    setSubmitError(null);
    setSubmitFailed(false);
    setSubmitting(true);
    try {
      const session = await api.post<{ payId: string; payUrl: string }>(
        "/payments/session",
        {
          tier: details.tier,
          amount: details.amount,
          customer: details.customer,
          acceptTerms: true,
          language: currentLanguage(),
          promoCode: details.promoCode,
        }
      );
      goToPayPage(session.payUrl, navigate);
    } catch (err) {
      // The code ran out or expired since it was applied: drop it so the
      // summary shows the full price again, and say why.
      if (err instanceof ApiError && err.status === 409 && details.promoCode) {
        setPromo(null);
        setPromoError(err.message);
        setSubmitting(false);
        return;
      }
      setSubmitError(err instanceof Error ? err.message : null);
      setSubmitFailed(true);
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <header className="border-b border-background-200/70 bg-background-50/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto h-16 px-4 md:px-6 flex items-center justify-between">
          <Logo to="/" />
          <div className="flex items-center gap-3 md:gap-4">
            <Link
              to="/pricing"
              className="text-sm font-medium text-foreground-700 hover:text-primary-600 whitespace-nowrap transition-colors"
            >
              {t("common.nav.pricing")}
            </Link>
            {user ? (
              <Link
                to="/profile"
                className="text-sm font-medium text-foreground-700 hover:text-primary-600 whitespace-nowrap transition-colors"
              >
                {t("common.nav.profile")}
              </Link>
            ) : (
              <Link
                to="/auth"
                state={{ from: checkoutPath }}
                className="text-sm font-medium text-foreground-700 hover:text-primary-600 whitespace-nowrap transition-colors"
              >
                {t("common.nav.signIn")}
              </Link>
            )}
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-10 md:py-16">
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors"
        >
          <i className="ri-arrow-left-line"></i>
          {t("common.actions.backToPricing")}
        </Link>

        <h1 className="mt-4 font-heading font-bold text-3xl md:text-4xl text-foreground-950">
          {t("checkout.title")}
        </h1>
        <p className="mt-2 text-foreground-600">{t("checkout.intro")}</p>

        <form
          onSubmit={handleSubmit}
          className="mt-10 grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8 items-start"
        >
          {reviewing ? (
            <OrderReview
              line={line}
              promo={promo}
              details={form}
              onEdit={() => {
                setReviewing(false);
                setSubmitFailed(false);
              }}
            />
          ) : (
            <div className="space-y-8">
              {/* Plan */}
              <section className="rounded-2xl border border-background-200/70 bg-background-50 p-6 md:p-8">
                <h2 className="font-heading font-bold text-lg text-foreground-950">{t("checkout.plan.title")}</h2>
                <p className="mt-1 text-sm text-foreground-600">{t("checkout.plan.subtitle")}</p>

                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                  {plans.map((p) => {
                    const selected = !isCustom && p.name === plan.name;
                    const pText = planText(t, p);
                    return (
                      <button
                        key={p.name}
                        type="button"
                        onClick={() => selectPlan(p.name)}
                        className={`text-left rounded-xl border px-4 py-4 transition-colors cursor-pointer ${
                          selected
                            ? "border-primary-500 bg-primary-50 ring-2 ring-primary-400/40"
                            : "border-background-300/60 hover:border-primary-300"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-foreground-950">{p.name}</span>
                          <i
                            className={`text-lg ${
                              selected
                                ? "ri-radio-button-line text-primary-600"
                                : "ri-checkbox-blank-circle-line text-foreground-300"
                            }`}
                          ></i>
                        </div>
                        <div className="mt-1 text-lg font-bold text-foreground-950">
                          {pText.price}
                          <span className="text-xs font-medium text-foreground-500">{pText.period}</span>
                        </div>
                        <div className="mt-1 text-xs text-primary-600 font-medium">
                          {pText.credits}
                        </div>
                      </button>
                    );
                  })}

                  <button
                    type="button"
                    onClick={() => selectCustom()}
                    className={`text-left rounded-xl border px-4 py-4 transition-colors cursor-pointer ${
                      isCustom
                        ? "border-primary-500 bg-primary-50 ring-2 ring-primary-400/40"
                        : "border-dashed border-background-300 hover:border-primary-300"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-foreground-950">
                        {t("checkout.custom.name")}
                      </span>
                      <i
                        className={`text-lg ${
                          isCustom
                            ? "ri-radio-button-line text-primary-600"
                            : "ri-checkbox-blank-circle-line text-foreground-300"
                        }`}
                      ></i>
                    </div>
                    <div className="mt-1 text-lg font-bold text-foreground-950">
                      {custom ? formatEur(custom.amountCents) : t("checkout.custom.yourPrice")}
                    </div>
                    <div className="mt-1 text-xs text-primary-600 font-medium">
                      {custom
                        ? t("checkout.custom.credits", {
                            count: custom.credits,
                            formatted: formatCredits(custom.credits),
                          })
                        : `${formatEur(CUSTOM_MIN_CENTS)}–${formatEur(CUSTOM_MAX_CENTS)}`}
                    </div>
                  </button>
                </div>

                {isCustom && (
                  <div className="mt-5 rounded-xl bg-background-100 p-4">
                    <label htmlFor="customAmount" className={labelClass}>
                      {t("checkout.custom.priceLabel")}
                    </label>
                    <input
                      id="customAmount"
                      name="customAmount"
                      type="text"
                      inputMode="decimal"
                      autoComplete="off"
                      value={customAmount}
                      onChange={(e) => selectCustom(e.target.value)}
                      placeholder="50"
                      aria-invalid={Boolean(customError)}
                      className={inputClass}
                    />
                    <p className="mt-2 text-xs" aria-live="polite">
                      {custom ? (
                        <span className="text-foreground-600">
                          {t("checkout.custom.summary", {
                            count: custom.credits,
                            formatted: formatCredits(custom.credits),
                            level: custom.level.name,
                          })}
                        </span>
                      ) : (
                        <span className="text-red-600">{customError}</span>
                      )}
                    </p>
                  </div>
                )}
              </section>

              {/* Contact details */}
              <section className="rounded-2xl border border-background-200/70 bg-background-50 p-6 md:p-8">
                <h2 className="font-heading font-bold text-lg text-foreground-950">
                  {t("checkout.contact.title")}
                </h2>
                <p className="mt-1 text-sm text-foreground-600">
                  {t("checkout.contact.subtitle")}
                  {!user && ` ${t("checkout.contact.creditsNote")}`}
                </p>

                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label htmlFor="fullName" className={labelClass}>
                      {t("checkout.fields.fullName")}
                    </label>
                    <input
                      id="fullName"
                      name="fullName"
                      type="text"
                      autoComplete="name"
                      value={form.fullName}
                      onChange={(e) => setField("fullName")(e.target.value)}
                      placeholder="Ivan Petrov"
                      className={inputClass}
                    />
                    {errors.fullName && (
                      <p className="mt-1.5 text-xs text-red-600">{t(`checkout.errors.${errors.fullName}`)}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="email" className={labelClass}>
                      {t("checkout.fields.email")}
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={(e) => setField("email")(e.target.value)}
                      placeholder="you@example.com"
                      className={inputClass}
                    />
                    {errors.email && (
                      <p className="mt-1.5 text-xs text-red-600">{t(`checkout.errors.${errors.email}`)}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="phone" className={labelClass}>
                      {t("checkout.fields.phone")}
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      autoComplete="tel"
                      value={form.phone}
                      onChange={(e) => setField("phone")(e.target.value)}
                      placeholder="+359 88 688 6479"
                      className={inputClass}
                    />
                    {errors.phone && (
                      <p className="mt-1.5 text-xs text-red-600">{t(`checkout.errors.${errors.phone}`)}</p>
                    )}
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="company" className={labelClass}>
                      {t("checkout.fields.company")}{" "}
                      <span className="text-foreground-400 font-normal">{t("checkout.fields.optional")}</span>
                    </label>
                    <input
                      id="company"
                      name="company"
                      type="text"
                      autoComplete="organization"
                      value={form.company}
                      onChange={(e) => setField("company")(e.target.value)}
                      placeholder={t("checkout.placeholders.company")}
                      className={inputClass}
                    />
                  </div>
                </div>
              </section>

              {/* Billing address */}
              <section className="rounded-2xl border border-background-200/70 bg-background-50 p-6 md:p-8">
                <h2 className="font-heading font-bold text-lg text-foreground-950">
                  {t("checkout.billing.title")}
                </h2>
                <p className="mt-1 text-sm text-foreground-600">{t("checkout.billing.subtitle")}</p>

                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label htmlFor="addressLine1" className={labelClass}>
                      {t("checkout.fields.addressLine1")}
                    </label>
                    <input
                      id="addressLine1"
                      name="addressLine1"
                      type="text"
                      autoComplete="address-line1"
                      value={form.addressLine1}
                      onChange={(e) => setField("addressLine1")(e.target.value)}
                      placeholder={t("checkout.placeholders.addressLine1")}
                      className={inputClass}
                    />
                    {errors.addressLine1 && (
                      <p className="mt-1.5 text-xs text-red-600">
                        {t(`checkout.errors.${errors.addressLine1}`)}
                      </p>
                    )}
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="addressLine2" className={labelClass}>
                      {t("checkout.fields.addressLine2")}{" "}
                      <span className="text-foreground-400 font-normal">{t("checkout.fields.optional")}</span>
                    </label>
                    <input
                      id="addressLine2"
                      name="addressLine2"
                      type="text"
                      autoComplete="address-line2"
                      value={form.addressLine2}
                      onChange={(e) => setField("addressLine2")(e.target.value)}
                      placeholder={t("checkout.placeholders.addressLine2")}
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label htmlFor="city" className={labelClass}>
                      {t("checkout.fields.city")}
                    </label>
                    <input
                      id="city"
                      name="city"
                      type="text"
                      autoComplete="address-level2"
                      value={form.city}
                      onChange={(e) => setField("city")(e.target.value)}
                      placeholder="Razgrad"
                      className={inputClass}
                    />
                    {errors.city && (
                      <p className="mt-1.5 text-xs text-red-600">{t(`checkout.errors.${errors.city}`)}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="postalCode" className={labelClass}>
                      {t("checkout.fields.postalCode")}
                    </label>
                    <input
                      id="postalCode"
                      name="postalCode"
                      type="text"
                      autoComplete="postal-code"
                      value={form.postalCode}
                      onChange={(e) => setField("postalCode")(e.target.value)}
                      placeholder="7200"
                      className={inputClass}
                    />
                    {errors.postalCode && (
                      <p className="mt-1.5 text-xs text-red-600">
                        {t(`checkout.errors.${errors.postalCode}`)}
                      </p>
                    )}
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="country" className={labelClass}>
                      {t("checkout.fields.country")}
                    </label>
                    <select
                      id="country"
                      name="country"
                      autoComplete="country-name"
                      value={form.country}
                      onChange={(e) => setField("country")(e.target.value)}
                      className={`${inputClass} cursor-pointer`}
                    >
                      {/* The value stays English: it goes to the bank, which takes Latin only. */}
                      {COUNTRIES.map((c) => (
                        <option key={c} value={c}>
                          {t(`checkout.countries.${c}`, { defaultValue: c })}
                        </option>
                      ))}
                    </select>
                    {errors.country && (
                      <p className="mt-1.5 text-xs text-red-600">{t(`checkout.errors.${errors.country}`)}</p>
                    )}
                  </div>
                </div>
              </section>
            </div>
          )}

          {/* Order summary */}
          <aside className="lg:sticky lg:top-8">
            <div className="rounded-2xl border border-background-200/70 bg-background-100 p-6 md:p-8">
              <h2 className="font-heading font-bold text-lg text-foreground-950">
                {t("checkout.summary.title")}
              </h2>

              <div className="mt-5 flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-foreground-950">{line.title}</p>
                  <p className="mt-0.5 text-xs text-foreground-600">
                    {line.credits}
                  </p>
                </div>
                <span className="text-sm font-semibold text-foreground-950 whitespace-nowrap">
                  {formatEur(line.priceCents)}
                </span>
              </div>

              <ul className="mt-4 space-y-2">
                {line.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-xs text-foreground-600">
                    <i className="ri-check-line text-primary-500 text-sm mt-0.5"></i>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6 pt-5 border-t border-background-300/60">
                <PromoCodeField
                  applied={promo}
                  error={promoError}
                  checking={promoChecking}
                  disabled={submitting || Boolean(customError)}
                  onApply={(code) => void applyPromo(code)}
                  onRemove={removePromo}
                />
              </div>

              <div className="mt-5 pt-5 border-t border-background-300/60 space-y-2">
                <div className="flex items-center justify-between text-sm text-foreground-600">
                  <span>{t("checkout.summary.subtotal")}</span>
                  <span>{formatEur(line.priceCents)}</span>
                </div>
                {promo && (
                  <div className="flex items-center justify-between text-sm text-primary-700">
                    <span>{t("checkout.promo.discount", { code: promo.code })}</span>
                    <span>−{formatEur(promo.discountCents)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm text-foreground-600">
                  <span>{t("checkout.summary.taxes")}</span>
                  <span>{t("checkout.summary.included")}</span>
                </div>
                <div className="flex items-center justify-between pt-2 text-base font-bold text-foreground-950">
                  <span>{t("checkout.summary.totalDueToday")}</span>
                  <span>{formatEur(totalCents)}</span>
                </div>
                <p className="pt-1 text-xs text-foreground-500">{t("common.prices.note")}</p>
              </div>

              <label className="mt-6 flex items-start gap-2.5 text-xs leading-relaxed text-foreground-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => {
                    setAcceptTerms(e.target.checked);
                    if (e.target.checked) setTermsError(false);
                  }}
                  aria-invalid={termsError}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-primary-600 cursor-pointer"
                />
                <span>
                  <Trans
                    i18nKey="checkout.acceptTerms"
                    components={{
                      terms: <Link to="/terms-of-use" target="_blank" className="text-primary-600 hover:underline" />,
                      refund: <Link to="/refund-policy" target="_blank" className="text-primary-600 hover:underline" />,
                      delivery: <Link to="/delivery-policy" target="_blank" className="text-primary-600 hover:underline" />,
                    }}
                  />
                </span>
              </label>
              {termsError && <p className="mt-1.5 text-xs text-red-600">{t("checkout.errors.acceptTerms")}</p>}

              <button
                type="submit"
                disabled={submitting || Boolean(customError)}
                className="mt-4 w-full whitespace-nowrap rounded-full bg-gradient-to-r from-primary-500 to-accent-500 py-3.5 text-sm font-semibold text-background-50 transition-transform hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100 cursor-pointer"
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-1.5"></i>
                    {t("checkout.opening")}
                  </>
                ) : reviewing ? (
                  t("checkout.orderNow", { price: formatEur(totalCents) })
                ) : (
                  <>
                    {t("checkout.reviewOrder")}
                    <i className="ri-arrow-right-line ml-1.5"></i>
                  </>
                )}
              </button>

              {submitFailed && (
                <p className="mt-3 text-center text-xs text-red-600">
                  {submitError ?? t("checkout.errors.startFailed")}
                </p>
              )}

              <p className="mt-4 text-center text-xs text-foreground-500 flex items-center justify-center gap-1.5">
                <i className="ri-lock-line text-primary-600"></i>
                {reviewing ? t("checkout.chooseBankNext") : t("checkout.reviewNext")}
              </p>

              <CardBrands secure pci className="mt-4 justify-center" />

              <MerchantOfRecord className="mt-5" />
            </div>
          </aside>
        </form>
      </main>

      <Footer />
    </div>
  );
}
